import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuFileUp, LuLoaderCircle, LuSend, LuTreePalm, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { Field, TextArea, TextInput } from '../../../components/form/FormControls';
import { auth, db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { calculateLeaveDays, computeAnnualBalance, getAnnualEntitlement, getCarryStartYear } from '../../../utils/leaveBalance';
import { CARD, HelpNote, SectionHeader } from '../components/ProfileUi';
import { REQUESTABLE_LEAVE_TYPES } from './leaveOptions';

const BALANCE_ITEMS = ['entitlement', 'used', 'reserved', 'remaining'];

/**
 * "İzin Talep Et": annual, excuse or sick leave. Days are counted from the
 * dates (Saturday half a day, Sunday and public holidays not at all); annual
 * leave can't exceed the remaining balance. The request goes to the managers
 * for approval.
 */
const LeaveRequestForm = ({ userData, employeeStartDate, employeeId, myLeaveRequests, onSubmitted }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.leave.form.${key}`, vars);
  const [selectedLeaveType, setSelectedLeaveType] = useState('yillik');
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');
  const [leaveDays, setLeaveDays] = useState('0');
  const [travelDays, setTravelDays] = useState('0');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveAddress, setLeaveAddress] = useState('');
  const [leaveReportFile, setLeaveReportFile] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Tarihlere göre izin günü (Cmt 0.5 / Paz 0 / hafta içi 1)
  useEffect(() => {
    if (leaveStartDate && leaveEndDate) setLeaveDays(String(calculateLeaveDays(leaveStartDate, leaveEndDate)));
  }, [leaveStartDate, leaveEndDate]);

  const annualLeaveBalance = useMemo(() => computeAnnualBalance({
    entitlement: getAnnualEntitlement(userData),
    leaveRequests: myLeaveRequests,
    employeeLeaves: [],
    year: new Date().getFullYear(),
    carryFromYear: getCarryStartYear({ startDate: employeeStartDate }),
  }), [userData, employeeStartDate, myLeaveRequests]);

  const requestedDays = Number(leaveDays) || 0;
  const isAnnual = selectedLeaveType === 'yillik';
  const overBalance = isAnnual && requestedDays > annualLeaveBalance.remaining;
  const noBalance = isAnnual && annualLeaveBalance.remaining <= 0;

  const resetForm = () => {
    setSelectedLeaveType('yillik');
    setLeaveStartDate('');
    setLeaveEndDate('');
    setLeaveDays('0');
    setTravelDays('0');
    setLeaveReason('');
    setLeaveAddress('');
    setLeaveReportFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLeaveRequestSubmit = async () => {
    if (!leaveStartDate || !leaveEndDate) {
      toast.error(label('errors.dates'));
      return;
    }
    if (!userData.eName || !userData.eSurname) {
      toast.error(label('errors.user'));
      return;
    }
    // Yıllık izin bakiyesi: hak yoksa ya da yetmiyorsa talep gönderilmez
    if (isAnnual) {
      if (requestedDays <= 0) {
        toast.error(label('errors.days'));
        return;
      }
      if (requestedDays > annualLeaveBalance.remaining) {
        toast.error(label('errors.balance', { remaining: annualLeaveBalance.remaining, requested: requestedDays }));
        return;
      }
    }

    setLeaveLoading(true);
    try {
      let fileMeta = null;
      if (leaveReportFile) {
        const ext = String(leaveReportFile.name).split('.').pop();
        const path = `leave-requests/${auth.currentUser.uid}/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, leaveReportFile);
        const url = await getDownloadURL(storageRef);
        fileMeta = { name: leaveReportFile.name, path, url, type: leaveReportFile.type || ext };
      }

      await addDoc(collection(db, 'LeaveRequests'), {
        employeeId: employeeId || auth.currentUser.uid,
        employeeName: `${userData.eName} ${userData.eSurname}`,
        employeeEmail: userData.eMail,
        leaveType: selectedLeaveType,
        startDate: leaveStartDate,
        endDate: leaveEndDate,
        leaveDays: Number(leaveDays),
        travelDays: Number(travelDays),
        reason: leaveReason,
        address: leaveAddress,
        reportFile: fileMeta,
        status: 'PENDING',
        createdAt: new Date(),
        requestType: 'LEAVE_REQUEST',
      });

      toast.success(label('sent'));
      onSubmitted();
      resetForm();
    } catch (error) {
      console.error('❌ İzin talebi gönderme hatası:', error);
      toast.error(label('errors.send'));
    }
    setLeaveLoading(false);
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuTreePalm}
        gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
        title={label('title')}
        description={label('description')}
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleLeaveRequestSubmit();
        }}
        className={`${CARD} tw-space-y-5 tw-p-4 sm:tw-p-5`}
      >
        {/* İzin türü */}
        <fieldset className="tw-space-y-2">
          <legend className="tw-mb-2 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
            {label('type')} <span aria-hidden="true" className="tw-text-rose-500">*</span>
          </legend>
          <div role="radiogroup" aria-label={label('type')} className="tw-grid tw-gap-2 sm:tw-grid-cols-3">
            {REQUESTABLE_LEAVE_TYPES.map((type) => {
              const selected = selectedLeaveType === type;
              return (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSelectedLeaveType(type)}
                  className={`tw-flex tw-flex-col tw-items-start tw-gap-0.5 tw-rounded-2xl tw-border tw-px-3.5 tw-py-3 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    selected
                      ? 'tw-border-teal-500/50 tw-bg-teal-500/[0.08] dark:tw-border-teal-400/40 dark:tw-bg-teal-400/10'
                      : 'tw-border-slate-200 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                  }`}
                >
                  <span className={`tw-text-sm tw-font-semibold ${selected ? 'tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>{t(`profilePage.leave.types.${type}`)}</span>
                  <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label(`typeHints.${type}`)}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Yıllık izin bakiyesi */}
        {isAnnual && (
          <div className="tw-space-y-2">
            <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
              {BALANCE_ITEMS.map((item) => {
                const value = annualLeaveBalance[item];
                const tone = item === 'remaining'
                  ? (value <= 0 ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-emerald-600 dark:tw-text-emerald-300')
                  : 'tw-text-slate-900 dark:tw-text-white';
                return (
                  <div key={item} className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <dd className={`tw-text-xl tw-font-semibold tw-leading-none tw-tabular-nums ${tone}`}>{value}</dd>
                    <dt className="tw-mt-1 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label(`balance.${item}`)}</dt>
                  </div>
                );
              })}
            </dl>
            {noBalance && <HelpNote tone="danger">{label('noBalance')}</HelpNote>}
          </div>
        )}

        {/* Tarihler */}
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('start')} htmlFor="leave-start" required>
            <TextInput id="leave-start" type="date" required value={leaveStartDate} onChange={(event) => setLeaveStartDate(event.target.value)} />
          </Field>
          <Field label={label('end')} htmlFor="leave-end" required hint={label('endHint')}>
            <TextInput id="leave-end" type="date" required min={leaveStartDate || undefined} value={leaveEndDate} onChange={(event) => setLeaveEndDate(event.target.value)} />
          </Field>
          <Field label={label('days')} htmlFor="leave-days" hint={label('daysHint')}>
            <TextInput id="leave-days" type="number" readOnly value={leaveDays} className="tw-bg-slate-100/80 dark:tw-bg-white/[0.02]" />
          </Field>
          <Field label={label('travelDays')} htmlFor="leave-travel" hint={label('travelDaysHint')}>
            <TextInput id="leave-travel" type="number" step="0.5" min="0" inputMode="decimal" value={travelDays} onChange={(event) => setTravelDays(event.target.value)} />
          </Field>
        </div>
        {overBalance && !noBalance && requestedDays > 0 && (
          <HelpNote tone="warning">{label('overBalance', { remaining: annualLeaveBalance.remaining, requested: requestedDays })}</HelpNote>
        )}

        <Field label={label('reason')} htmlFor="leave-reason">
          <TextArea id="leave-reason" rows={3} value={leaveReason} onChange={(event) => setLeaveReason(event.target.value)} placeholder={label('reasonPlaceholder')} />
        </Field>
        <Field label={label('address')} htmlFor="leave-address" hint={label('addressHint')}>
          <TextInput id="leave-address" value={leaveAddress} onChange={(event) => setLeaveAddress(event.target.value)} placeholder={label('addressPlaceholder')} />
        </Field>

        <Field label={label('report')} htmlFor="leave-report" hint={label('reportHint')}>
          <input
            ref={fileInputRef}
            id="leave-report"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={(event) => setLeaveReportFile(event.target.files[0] || null)}
            className="tw-hidden"
          />
          {leaveReportFile ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
              <LuFileUp aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-teal-600" />
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-slate-700 dark:tw-text-slate-200">{leaveReportFile.name}</span>
              <button
                type="button"
                onClick={() => {
                  setLeaveReportFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label={label('removeReport')}
                className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            </div>
          ) : (
            <Button onClick={() => fileInputRef.current?.click()}>
              <LuFileUp aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('chooseReport')}
            </Button>
          )}
        </Field>

        <div className="tw-flex tw-flex-col-reverse tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('approvalNote')}</p>
          <Button variant="primary" type="submit" disabled={leaveLoading || noBalance} className="max-sm:tw-w-full">
            {leaveLoading ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {leaveLoading ? label('sending') : label('submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};

LeaveRequestForm.propTypes = {
  userData: PropTypes.object.isRequired,
  employeeStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  employeeId: PropTypes.string,
  myLeaveRequests: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export default LeaveRequestForm;
